import type { ApiOrder } from '../api/types'
import {
  CHECKOUT_OUTCOME,
  CHECKOUT_PAYMENT_CONFIRM,
  ORDER_STATUS,
  PAYMENT_STATUS,
  type CheckoutOutcome,
} from '../constants/orders'
import { PAYMENT_METHOD } from '../constants/settings'

export function needsPaymentConfirmation(order: ApiOrder): boolean {
  return (
    order.paymentStatus === PAYMENT_STATUS.unpaid &&
    order.paymentMethod !== PAYMENT_METHOD.cod
  )
}

export function checkoutPaymentOutcome(orders: ApiOrder[]): CheckoutOutcome {
  if (orders.length > 0 && orders.every((order) => order.status === ORDER_STATUS.cancelled)) {
    return CHECKOUT_OUTCOME.cancelled
  }
  if (orders.some((order) => order.paymentStatus === PAYMENT_STATUS.refunded)) {
    return CHECKOUT_OUTCOME.refunded
  }
  if (orders.some(needsPaymentConfirmation)) {
    return CHECKOUT_OUTCOME.pending
  }
  if (orders.every((order) => order.paymentMethod === PAYMENT_METHOD.cod)) {
    return CHECKOUT_OUTCOME.cod
  }
  return CHECKOUT_OUTCOME.paid
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export async function waitForCheckoutPayment(
  orders: ApiOrder[],
  loadOrder: (id: string) => Promise<ApiOrder>,
  timing: { timeoutMs: number; intervalMs: number } = CHECKOUT_PAYMENT_CONFIRM,
): Promise<ApiOrder[]> {
  if (!orders.some(needsPaymentConfirmation)) return orders

  const deadline = Date.now() + timing.timeoutMs
  let latest = orders

  while (Date.now() < deadline) {
    latest = await Promise.all(
      latest.map(async (order) => {
        if (!needsPaymentConfirmation(order)) return order
        try {
          return await loadOrder(order.id)
        } catch {
          return order
        }
      }),
    )
    if (!latest.some(needsPaymentConfirmation)) return latest

    const remaining = deadline - Date.now()
    if (remaining <= 0) break
    await delay(Math.min(timing.intervalMs, remaining))
  }

  return latest
}
